import AccidentsService from 'api/accidents/AccidentsService';
import { AccidentDto } from 'api/accidents/models/AccidentDto';
import { AccidentsSearchCriteria } from 'api/accidents/models/AccidentsSearchCriteria';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import InputSearch from 'common/components/inputSearch/InputSearch';
import Popover from 'common/components/popover/Popover';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE, MAX_PAGINATION_ITEMS_PER_PAGE } from 'Config';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { Types } from 'screens/vehicle/VehicleScreen';
import { Reducers } from 'store/types';
import { useDebouncedCallback } from 'use-debounce';
import AccidentFiltersScreen, { Filters } from '../AccidentFilters/AccidentFiltersScreen';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import styles from './ListAccidentsScreen.module.scss';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';
import AccidentListTable from './AccidentListTable';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';

export type Props = {
    onClickItem: (item: AccidentDto, isDetails: boolean) => void;
    createNewAccident?: () => void;
    isDriver?: boolean;
    criteriaProp?: AccidentsSearchCriteria;
    isVehicle?: boolean;
    editable: boolean;
    tourReRender?: () => void;
} & React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLDivElement>, HTMLDivElement>;


const ListAccidentsScreen = ({ onClickItem, createNewAccident, isDriver, criteriaProp, isVehicle, editable, tourReRender, ...props }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const isFirstLoad = useRef(true);
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);

    const { vehicleId, driverId } = useParams<{ vehicleId: string, driverId: string, type: Types, tabId: string, itemType: string, itemId: string }>();

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasAccidentsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_ACCIDENTS_WRITE']);

    const filterJsonKey = 'ACCIDENTSFILTER-'.concat(isVehicle ? 'v-'.concat(vehicleId) : isDriver ? 'd-'.concat(driverId) : '0');

    const FILTERS_CACHE_KEY = filterJsonKey;

    const [accidents, setAccidents] = useState<AccidentDto[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const bidirectionalScroll = useBidirectionalScroll({
        cacheKey: FILTERS_CACHE_KEY,
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });

    const {
        loadedPageRange,
        isLoadingPrevious,
        scrollToRowId: scrollToItemId,
        shouldLoadAdjacentPages,
        setLoadedPageRange,
        setIsLoadingPrevious,
        resetScrollState,
        calculatePageFromIndex,
        prepareLoadPrevious,
        adjustScrollAfterPrepend,
        finishRestoration,
        initiateRestoration,
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
    } = bidirectionalScroll;

    const [criteria, setCriteria] = useState<AccidentsSearchCriteria>(!criteriaProp ? {
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        vehicleId: vehicleId,
        driverId: isDriver ? driverId : undefined,
        orderColumn: 'a.date',
        orderBy: 'desc',
    } : { ...criteriaProp });

    const [filters, setFilters] = useState<Filters>({});
    const [filtersTotal, setFiltersTotal] = useState(0);
    const previousCriteria = useRef<AccidentsSearchCriteria>(criteria);
    const [reRenderSearch] = useState<number>(0);

    const getData = async (isLoadMore = false) => {
        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            saveOrdernationFilters();

            const _accidents = await AccidentsService.getList(criteria);
            setTotalItems(_accidents.totalItems);

            if (isLoadMore) {
                setAccidents(prev => [...prev, ..._accidents.items]);
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                setAccidents(_accidents.items);
            }

            Loading.hide();
            setIsLoadingMore(false);

            if (shouldLoadAdjacentPages.current) {
                shouldLoadAdjacentPages.current = false;
                const currentPage = criteria.page;
                const totalPages = Math.ceil(_accidents.totalItems / (criteria.itemsPerPage || DEFAULT_PAGINATION_ITEMS_PER_PAGE));

                if (currentPage > 1) {
                    try {
                        const prevCriteria = { ...criteria, page: currentPage - 1 };
                        const prevResult = await AccidentsService.getList(prevCriteria);
                        setAccidents(prev => [...prevResult.items, ...prev]);
                        setLoadedPageRange(prev => ({ ...prev, min: currentPage - 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.ACCIDENTS, `Couldn't load previous page`, e);
                    }
                }

                if (currentPage < totalPages) {
                    try {
                        const nextCriteria = { ...criteria, page: currentPage + 1 };
                        const nextResult = await AccidentsService.getList(nextCriteria);
                        setAccidents(prev => [...prev, ...nextResult.items]);
                        setLoadedPageRange(prev => ({ ...prev, max: currentPage + 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.ACCIDENTS, `Couldn't load next page`, e);
                    }
                }

                finishRestoration();
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.ACCIDENTS, `Couldn't get accident list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
            setIsLoadingMore(false);
            resetScrollState();
        } finally {
            tourReRender && tourReRender();
        }
    };

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setAccidents([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({ ...prev, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field }));
    };

    const loadMore = useCallback(() => {
        if (isLoadingMore || accidents.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, accidents.length, totalItems, loadedPageRange.max]);

    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;

        setIsLoadingPrevious(true);
        const prevPage = loadedPageRange.min - 1;

        try {
            prepareLoadPrevious();

            const prevCriteria: AccidentsSearchCriteria = {
                ...criteria,
                page: prevPage,
                itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
            };
            const result = await AccidentsService.getList(prevCriteria);

            setAccidents(prev => [...result.items, ...prev]);
            setLoadedPageRange(prev => ({ ...prev, min: prevPage }));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.ACCIDENTS, `Couldn't get previous accident page`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setIsLoadingPrevious(false);
        }
    }, [isLoadingPrevious, loadedPageRange.min, criteria, addToast, t]);

    const fetchAllForExport = useCallback(async () => {
        const result = await AccidentsService.getList({
            ...criteria,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        return result.items;
    }, [criteria]);

    useEffect(() => {
        if (!isLoadingPrevious) {
            adjustScrollAfterPrepend();
        }
    }, [accidents, isLoadingPrevious, adjustScrollAfterPrepend]);

    useEffect(() => {
        if (isFirstLoad.current) {
            loadFilters();
        } else {
            const isLoadMore = criteria.page > loadedPageRange.max && accidents.length > 0;
            getData(isLoadMore);
            previousCriteria.current = criteria;
        }
    }, [criteria]);

    const saveFiltersOnCache = (f: Filters) => {
        setFilters(prev => ({ ...prev, ...f }));
        if ((f as any).scrollToItemId && f.page) {
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(f));
        } else {
            const { page: _page, ...filtersWithoutPage } = f;
            const { scrollToItemId: _scrollToItemId, ...rest } = filtersWithoutPage as any;
            localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(rest));
        }
    };

    const saveOrdernationFilters = () => {
        const newFilters = { ...filters };
        newFilters.page = criteria.page;
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        newFilters.allIn = criteria.allIn;
        saveFiltersOnCache(newFilters);
    };

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem(FILTERS_CACHE_KEY);

        // When the parent screen owns the filters (criteriaProp), the cache only
        // holds ordering state — applying it as filters would overwrite the
        // criteria the parent just selected.
        if (criteriaProp) {
            isFirstLoad.current = false;
            if (cachedFilters != null) {
                const f: Filters = JSON.parse(cachedFilters) as Filters;
                if (f.orderBy && f.orderColumn) {
                    setCriteria(prev => ({ ...prev, orderBy: f.orderBy, orderColumn: f.orderColumn }));
                    return;
                }
            }
            getData();
            return;
        }

        if (cachedFilters != null) {
            const f: Filters = JSON.parse(cachedFilters) as Filters;

            if (isDriver) {
                f.driverId = driverId;
                f.vehicleId = undefined;
            }

            if (isVehicle) {
                f.vehicleId = vehicleId;
            }

            if ((f as any).scrollToItemId) {
                const startPage = initiateRestoration({
                    page: f.page || 1,
                    scrollToItemId: (f as any).scrollToItemId,
                });

                const cleanedFilters = { ...f, scrollToItemId: undefined, page: 1 };
                localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(cleanedFilters));
                setFilters(cleanedFilters);

                setCriteria(prev => ({
                    ...prev,
                    driverId: f.driverId,
                    startDate: f.startDate,
                    endDate: f.endDate,
                    guilty: f.guilty,
                    insuranceAssumedCost: f.insuranceAssumedCost,
                    orderBy: f.orderBy,
                    orderColumn: f.orderColumn,
                    allIn: f.allIn,
                    page: startPage,
                    itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
                }));
                isFirstLoad.current = false;
                return;
            }

            onChangeFilters(f, true);
        } else {
            getData();
        }

        isFirstLoad.current = false;
    };

    const debounced = useDebouncedCallback((value: string) => {
        if (value === criteria.allIn) return;
        setAccidents([]);
        resetScrollState();
        setCriteria(prev => ({ ...prev, page: 1, allIn: value }));
    }, 500);

    const onChangeFilters = (f: Filters, isOnLoad?: boolean) => {
        updateTotalFilters(f);
        setFilters(f);

        if (!isOnLoad) {
            setAccidents([]);
            resetScrollState();
        }

        setCriteria(prev => ({
            ...prev,
            driverId: f.driverId,
            startDate: f.startDate,
            endDate: f.endDate,
            guilty: f.guilty,
            insuranceAssumedCost: f.insuranceAssumedCost,
            page: (isOnLoad && f.page) ? f.page : 1,
            orderBy: f.orderBy,
            orderColumn: f.orderColumn,
            allIn: f.allIn,
        }));

        saveFiltersOnCache(f);
    };

    const updateTotalFilters = (f: Filters) => {
        let total = 0;

        if (f.startDate || f.endDate) {
            total++;
        }

        if (f.allIn) {
            total++;
        }

        if (f.guilty === false || f.guilty === true) {
            total++;
        }
        if (f.insuranceAssumedCost === false || f.insuranceAssumedCost === true) {
            total++;
        }

        if (f.vehicleId && f.vehicleId.length > 0 && !isVehicle) {
            total++;
        }

        if (f.driverId && f.driverId.length > 0 && !isDriver) {
            total++;
        }

        setFiltersTotal(total);
    };

    const onRowClick = (item: AccidentDto, isDetails: boolean) => {
        const itemIndex = accidents.findIndex(a => a.id === item.id);
        const actualPage = calculatePageFromIndex(itemIndex);

        const newFilters: any = { ...filters };
        newFilters.page = actualPage;
        newFilters.scrollToItemId = item.id;
        newFilters.orderBy = criteria.orderBy;
        newFilters.orderColumn = criteria.orderColumn;
        saveFiltersOnCache(newFilters);

        onClickItem(item, isDetails);
    };

    const onDeleteFromList = () => {
        setAccidents([]);
        resetScrollState();
        setCriteria(prev => ({ ...prev, page: 1 }));
    };

    return (
        <div className={styles.listWrapper} {...props}>
            <div className={styles.searchSection}>
                <div className={styles.tabHeader}>
                    <div>
                        <InputSearch defaultValue={criteria.allIn} onChangeValue={debounced} inputContainerClassName={styles.searchInputContainer} placeholder={t('accidents.list.search')} />
                    </div>
                    <div className={styles.rowFlex}>
                        <ExportListingButton exportRef={exportListingRef} />
                        {!criteriaProp && <Popover
                            contentContainerClassName={styles.filtersPopoverContainer}
                            containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                            positions={['left']}
                            onClickOutside={() => updateTotalFilters(filters)}
                            content={setIsPopoverOpen =>
                                <AccidentFiltersScreen
                                    isVehicle={isVehicle}
                                    isDriver={isDriver}
                                    filters={filters}
                                    onFilter={f => { setIsPopoverOpen(false); onChangeFilters(f) }}
                                    onChange={f => updateTotalFilters(f)}
                                />
                            }
                        >
                            {(isPopoverOpen, setIsPopoverOpen) => (
                                <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                    {filtersTotal > 0 &&
                                        <div className={styles.counterList}> <div className={styles.counterNumber}>{filtersTotal}</div> </div>
                                    }
                                </ScreenHeaderButton>
                            )}
                        </Popover>}
                        {createNewAccident && hasAccidentsWritePolicy && editable && <ScreenHeaderButton icon={AddIcon} onClick={() => createNewAccident()} />}
                    </div>
                </div>
            </div>

            {(accidents.length > 0 || totalItems === 0) && !isFirstLoad.current ? (
                <div className={styles.listTblWrapper}>
                    <AccidentListTable
                        reRenderSearch={reRenderSearch}
                        items={accidents}
                        totalItems={totalItems}
                        isLoadingMore={isLoadingMore}
                        hasMore={getHasMore(accidents.length, totalItems)}
                        isVehicle={isVehicle}
                        isDriver={isDriver}
                        editable={editable}
                        onLoadMore={getOnLoadMore(loadMore)}
                        onTableFilter={onTableFilter}
                        onClickItem={onRowClick}
                        onDeleteFromList={onDeleteFromList}
                        criteria={criteria}
                        scrollToRowId={scrollToItemId}
                        hasMorePrevious={getHasMorePrevious()}
                        isLoadingPrevious={isLoadingPrevious}
                        onLoadPrevious={getOnLoadPrevious(loadPrevious)}
                        onFetchAllForExport={fetchAllForExport}
                        exportRef={exportListingRef}
                    />
                </div>
            ) : null}
        </div>
    );
};

export default ListAccidentsScreen;
